// File: src/pages/HR/HRApprovalCenter.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Tabs, Tab, CircularProgress, Alert, Paper,
    Button, Modal, TextField, Stack, Table, TableHead, TableRow,
    TableCell, TableBody, Chip, Tooltip, Grid, Divider,
    MenuItem, Select, FormControl, InputLabel, IconButton
} from '@mui/material';
import {
    InfoOutlined, LocationOn, AccessTime, CheckCircle,
    Warning, CameraAlt, Person, Refresh, FilterList, CalendarMonth,
    ZoomIn, ZoomOut, ZoomOutMap, Close
} from '@mui/icons-material';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import AuthenticatedImage from '../../components/AuthenticatedImage';

/* ─── Modal Styles ─── */
const MODAL_STYLE = {
    position: 'absolute', top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 440, bgcolor: 'background.paper',
    boxShadow: 24, p: 4, borderRadius: 3
};

const LIGHTBOX_STYLE = {
    position: 'absolute', top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: '#0f172a', borderRadius: 3, p: 2,
    outline: 'none', width: '92vw', maxWidth: 900,
    maxHeight: '96vh',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    boxShadow: 24, overflow: 'hidden'
};

const fmt = (v) => v || '-';

/* ─── helpers ─── */
const isLateRecord = (record) => {
    if (!record) return false;
    if (record.lateMinutes > 0) return true;
    if (record.late === true) return true;
    if (record.clockInTime) {
        const parts = record.clockInTime.split(':');
        const h = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        return h > 9 || (h === 9 && m > 40);
    }
    return false;
};

const formatTime = (t) => {
    if (!t || t === '00:00:00' || t === '00:00') return null;
    const parts = t.split(':');
    if (parts.length < 2) return null;
    const h = parts[0].padStart(2, '0');
    const m = parts[1].substring(0, 2).padStart(2, '0');
    return `${h}:${m}`;
};

const getTodayStr = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now - offset).toISOString().split('T')[0];
};

/* ─── Escalation detection ─── */
const isEscalated = (item) => {
    if (!item) return false;
    const s = String(item?.status || item?.approvalStatus || '').toUpperCase();
    return s === 'PENDING_MANAGER' && !item.approvalManagerRemark;
};

const isEscalatedLeave = (item) => {
    const s = String(item?.status || '').toUpperCase();
    return s === 'PENDING_MANAGER' && !item?.approvalManagerRemark;
};

const AMRemarkBadge = ({ remark }) => {
    if (!remark) return null;
    return (
        <Tooltip title={`Approval Manager Remark: ${remark}`} arrow>
            <Chip size="small" icon={<InfoOutlined sx={{ fontSize: 14 }} />} label="AM Remark"
                sx={{ bgcolor: '#fef9c3', color: '#854d0e', border: '1px solid #fde047', fontWeight: 700, fontSize: 11, cursor: 'help', mt: 0.5 }} />
        </Tooltip>
    );
};

const statusLabel = (s) => {
    const map = {
        PENDING_APPROVAL_MANAGER: 'Pending AM', PENDING_MANAGER: 'Pending Mgr',
        PENDING_HR: 'Pending HR', PENDING: 'Pending', APPROVED: 'Approved',
        APPROVED_BY_APPROVAL_MANAGER: 'Approved by AM', APPROVAL_MANAGER_APPROVED: 'Approved by AM',
    };
    return map[s] || s;
};

/* ═══════════════════════════════════════════════════════════════
    LIGHTBOX — Full image viewer with zoom in/out
═══════════════════════════════════════════════════════════════ */
const ImageLightbox = ({ open, src, employeeName, title, onClose }) => {
    const [zoom, setZoom] = useState(1);
    const [dragging, setDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const imgRef = useRef(null);

    useEffect(() => {
        if (open) { setZoom(1); setPosition({ x: 0, y: 0 }); }
    }, [open, src]);

    useEffect(() => {
        const handleKey = (e) => {
            if (!open) return;
            if (e.key === 'Escape') onClose();
            if (e.key === '+' || e.key === '=') setZoom(z => Math.min(z + 0.25, 4));
            if (e.key === '-') setZoom(z => Math.max(z - 0.25, 0.5));
            if (e.key === '0') { setZoom(1); setPosition({ x: 0, y: 0 }); }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [open, onClose]);

    const handleWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.15 : 0.15;
        setZoom(z => Math.min(Math.max(z + delta, 0.5), 4));
    };

    const handleMouseDown = (e) => {
        if (zoom <= 1) return;
        setDragging(true);
        setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
    };

    const handleMouseMove = (e) => {
        if (!dragging) return;
        setPosition({ x: e.clientX - startPos.x, y: e.clientY - startPos.y });
    };

    const handleMouseUp = () => setDragging(false);

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={LIGHTBOX_STYLE}>
                {/* Top bar */}
                <Stack direction="row" alignItems="center" justifyContent="space-between"
                    sx={{ width: '100%', mb: 1.5 }}>
                    <Typography color="white" fontWeight={700} fontSize={14}>
                        {employeeName} — {title}
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: '#94a3b8' }} size="small">
                        <Close />
                    </IconButton>
                </Stack>

                {/* Image container */}
                <Box
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    sx={{
                        width: '100%',
                        height: '70vh',
                        overflow: 'hidden',
                        borderRadius: 2,
                        bgcolor: '#1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'default',
                        userSelect: 'none',
                        position: 'relative'
                    }}
                >
                    {src && (
                        <Box
                            ref={imgRef}
                            sx={{
                                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                                transformOrigin: 'center center',
                                transition: dragging ? 'none' : 'transform 0.15s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <AuthenticatedImage
                                src={src}
                                size="100%"
                                sx={{
                                    maxWidth: '80vw',
                                    maxHeight: '65vh',
                                    objectFit: 'contain',
                                    borderRadius: 1,
                                    display: 'block',
                                    pointerEvents: 'none',
                                }}
                            />
                        </Box>
                    )}

                    {/* Zoom % badge */}
                    <Box sx={{
                        position: 'absolute', bottom: 10, right: 10,
                        bgcolor: 'rgba(0,0,0,0.6)', borderRadius: 1,
                        px: 1.5, py: 0.5
                    }}>
                        <Typography fontSize={11} color="white" fontWeight={600}>
                            {Math.round(zoom * 100)}%
                        </Typography>
                    </Box>
                </Box>

                {/* Controls */}
                <Stack direction="row" spacing={1} alignItems="center" mt={1.5}>
                    <IconButton
                        onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))}
                        disabled={zoom <= 0.5}
                        sx={{ color: 'white', bgcolor: '#1e293b', border: '1px solid #334155',
                            '&:hover': { bgcolor: '#334155' }, '&:disabled': { color: '#475569' } }}
                        size="small"
                    >
                        <ZoomOut />
                    </IconButton>

                    <Button
                        onClick={() => { setZoom(1); setPosition({ x: 0, y: 0 }); }}
                        size="small"
                        sx={{ color: '#94a3b8', fontSize: 11, minWidth: 60,
                            border: '1px solid #334155', borderRadius: 1.5 }}
                    >
                        Reset
                    </Button>

                    <IconButton
                        onClick={() => setZoom(z => Math.min(z + 0.25, 4))}
                        disabled={zoom >= 4}
                        sx={{ color: 'white', bgcolor: '#1e293b', border: '1px solid #334155',
                            '&:hover': { bgcolor: '#334155' }, '&:disabled': { color: '#475569' } }}
                        size="small"
                    >
                        <ZoomIn />
                    </IconButton>

                    <IconButton
                        onClick={() => setZoom(2.5)}
                        sx={{ color: 'white', bgcolor: '#1e293b', border: '1px solid #334155',
                            '&:hover': { bgcolor: '#334155' } }}
                        size="small"
                    >
                        <ZoomOutMap />
                    </IconButton>

                    <Typography fontSize={11} color="#64748b" sx={{ ml: 1 }}>
                        Scroll to zoom · Drag to pan · +/- keys · 0 to reset
                    </Typography>
                </Stack>

                {/* Quick zoom buttons */}
                <Stack direction="row" spacing={0.5} mt={1}>
                    {[0.5, 1, 1.5, 2, 3, 4].map(z => (
                        <Button key={z} size="small" onClick={() => { setZoom(z); setPosition({ x: 0, y: 0 }); }}
                            sx={{
                                fontSize: 10, minWidth: 40, py: 0.3,
                                color: zoom === z ? 'white' : '#64748b',
                                bgcolor: zoom === z ? '#3b82f6' : 'transparent',
                                border: `1px solid ${zoom === z ? '#3b82f6' : '#334155'}`,
                                borderRadius: 1,
                                '&:hover': { bgcolor: zoom === z ? '#2563eb' : '#1e293b' }
                            }}>
                            {z}x
                        </Button>
                    ))}
                </Stack>
            </Box>
        </Modal>
    );
};

/* ═══════════════════════════════════════════════════════════════
    TODAY'S SELFIES SECTION
═══════════════════════════════════════════════════════════════ */
const TodaySelfiesSection = ({ records = [], loading = false, onRefresh, selectedDate, onDateChange, onImageClick }) => {
    const [branchFilter, setBranchFilter] = useState('ALL');

    const safeRecords = Array.isArray(records) ? records : [];
    const branches = ['ALL', ...Array.from(new Set(safeRecords.map(r => r.branchName).filter(Boolean)))];

    const filtered = branchFilter === 'ALL'
        ? safeRecords
        : safeRecords.filter(r => r.branchName === branchFilter);

    const clockedIn = filtered.filter(r => r.clockInTime && formatTime(r.clockInTime));
    const notClockedIn = filtered.filter(r => !r.clockInTime || !formatTime(r.clockInTime));
    const lateCount = clockedIn.filter(r => isLateRecord(r)).length;
    const onTimeCount = clockedIn.filter(r => !isLateRecord(r)).length;

    return (
        <Paper sx={{ p: 3, mt: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }} mb={2} gap={1.5} flexWrap="wrap">
                <Box>
                    <Typography variant="h6" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CameraAlt sx={{ color: '#6366f1' }} />
                        Attendance Selfies
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Selfies, GPS locations and attendance time for your mapped employees
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1}>
                    <Chip label={`${lateCount} Late`} size="small" color="error" sx={{ fontWeight: 700 }} />
                    <Chip label={`${onTimeCount} On Time`} size="small" color="success" sx={{ fontWeight: 700 }} />
                    <Chip label={`${notClockedIn.length} Not In`} size="small" color="default" sx={{ fontWeight: 700 }} />
                </Stack>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} alignItems="center">
                <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarMonth sx={{ fontSize: 18, color: '#6366f1' }} />
                    <TextField type="date" size="small" label="Date" value={selectedDate}
                        onChange={e => onDateChange(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: getTodayStr() }}
                        sx={{ width: 160 }} />
                </Stack>

                {branches.length > 2 && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FilterList sx={{ fontSize: 18, color: '#6366f1' }} />
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Branch</InputLabel>
                            <Select value={branchFilter} label="Branch"
                                onChange={e => setBranchFilter(e.target.value)}>
                                {branches.map(b => (
                                    <MenuItem key={b} value={b}>{b === 'ALL' ? 'All Branches' : b}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                )}

                <Button size="small" variant="outlined" startIcon={<Refresh />}
                    onClick={onRefresh} sx={{ borderRadius: 2, fontSize: 12, ml: 'auto' }}>
                    Refresh
                </Button>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                </Box>
            ) : safeRecords.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No employees mapped to you have attendance records for {selectedDate}.
                </Alert>
            ) : (
                <>
                    {clockedIn.length > 0 && (
                        <Box mb={3}>
                            <Typography fontWeight={700} fontSize={13} color="text.secondary" mb={1.5}
                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Clocked In — {clockedIn.length} employees
                            </Typography>
                            <Grid container spacing={2}>
                                {clockedIn.map((record, idx) => {
                                    const late = isLateRecord(record);
                                    const hasInSelfie = !!record.clockInSelfieUrl;
                                    const hasOutSelfie = !!record.clockOutSelfieUrl;
                                    const clockedOut = hasOutSelfie || (record.clockOutTime && formatTime(record.clockOutTime));
                                    const inLat = record.clockInLatitude;
                                    const inLng = record.clockInLongitude;
                                    const outLat = record.clockOutLatitude;
                                    const outLng = record.clockOutLongitude;

                                    return (
                                        <Grid item xs={12} sm={6} md={4} lg={3} key={record.id || idx}>
                                            <Paper elevation={0} sx={{
                                                p: 2, borderRadius: 3,
                                                border: `2px solid ${late ? '#fca5a5' : '#86efac'}`,
                                                bgcolor: late ? '#fff7f7' : '#f0fdf4',
                                                position: 'relative', height: '100%'
                                            }}>
                                                <Chip size="small"
                                                    icon={late
                                                        ? <Warning sx={{ fontSize: '13px !important' }} />
                                                        : <CheckCircle sx={{ fontSize: '13px !important' }} />}
                                                    label={late ? 'LATE' : 'ON TIME'}
                                                    sx={{
                                                        position: 'absolute', top: 10, right: 10,
                                                        bgcolor: late ? '#fef2f2' : '#f0fdf4',
                                                        color: late ? '#dc2626' : '#16a34a',
                                                        border: `1px solid ${late ? '#fca5a5' : '#86efac'}`,
                                                        fontWeight: 800, fontSize: 10
                                                    }}
                                                />

                                                <Stack direction="row" alignItems="center" spacing={1} mb={1.5} pr={6}>
                                                    <Person sx={{ fontSize: 16, color: '#6b7280', flexShrink: 0 }} />
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography fontWeight={700} fontSize={13} lineHeight={1.2}
                                                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {record.employeeName || '-'}
                                                        </Typography>
                                                        <Typography fontSize={11} color="text.secondary">
                                                            {record.designation || ''}{record.branchName ? ` · ${record.branchName}` : ''}
                                                        </Typography>
                                                    </Box>
                                                </Stack>

                                                {/* Selfie thumbnails */}
                                                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography fontSize={10} color="text.secondary" fontWeight={600} mb={0.5}>CLOCK IN</Typography>
                                                        {hasInSelfie ? (
                                                            <Tooltip title="Click to view full image" arrow>
                                                                <Box onClick={() => onImageClick(`/attendance/selfie/${record.clockInSelfieUrl}`, record.employeeName, 'Clock In')}
                                                                    sx={{ cursor: 'pointer', position: 'relative', '&:hover .zoom-hint': { opacity: 1 } }}>
                                                                    <AuthenticatedImage
                                                                        src={`/attendance/selfie/${record.clockInSelfieUrl}`}
                                                                        size="100%"
                                                                        sx={{
                                                                            height: 90, objectFit: 'cover', borderRadius: 2,
                                                                            border: `2px solid ${late ? '#fca5a5' : '#86efac'}`,
                                                                            display: 'block', width: '100%'
                                                                        }}
                                                                    />
                                                                    <Box className="zoom-hint" sx={{
                                                                        position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)',
                                                                        borderRadius: 2, display: 'flex', alignItems: 'center',
                                                                        justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s'
                                                                    }}>
                                                                        <ZoomIn sx={{ color: 'white', fontSize: 28 }} />
                                                                    </Box>
                                                                </Box>
                                                            </Tooltip>
                                                        ) : (
                                                            <Box sx={{ width: '100%', height: 90, borderRadius: 2, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #cbd5e1' }}>
                                                                <Typography fontSize={10} color="text.disabled">No photo</Typography>
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    {clockedOut && (
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography fontSize={10} color="text.secondary" fontWeight={600} mb={0.5}>CLOCK OUT</Typography>
                                                            {hasOutSelfie ? (
                                                                <Tooltip title="Click to view full image" arrow>
                                                                    <Box onClick={() => onImageClick(`/attendance/selfie/${record.clockOutSelfieUrl}`, record.employeeName, 'Clock Out')}
                                                                        sx={{ cursor: 'pointer', position: 'relative', '&:hover .zoom-hint': { opacity: 1 } }}>
                                                                        <AuthenticatedImage
                                                                            src={`/attendance/selfie/${record.clockOutSelfieUrl}`}
                                                                            size="100%"
                                                                            sx={{
                                                                                height: 90, objectFit: 'cover', borderRadius: 2,
                                                                                border: '2px solid #93c5fd',
                                                                                display: 'block', width: '100%'
                                                                            }}
                                                                        />
                                                                        <Box className="zoom-hint" sx={{
                                                                            position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)',
                                                                            borderRadius: 2, display: 'flex', alignItems: 'center',
                                                                            justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s'
                                                                        }}>
                                                                            <ZoomIn sx={{ color: 'white', fontSize: 28 }} />
                                                                        </Box>
                                                                    </Box>
                                                                </Tooltip>
                                                            ) : (
                                                                <Box sx={{ width: '100%', height: 90, borderRadius: 2, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #cbd5e1' }}>
                                                                    <Typography fontSize={10} color="text.disabled">No photo</Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>

                                                <Stack direction="row" alignItems="center" spacing={0.5} mb={0.4}>
                                                    <AccessTime sx={{ fontSize: 13, color: late ? '#dc2626' : '#16a34a' }} />
                                                    <Typography fontSize={12} fontWeight={700} color={late ? '#dc2626' : '#16a34a'}>
                                                        In: {formatTime(record.clockInTime) || '-'}
                                                        {late && record.lateMinutes > 0 && (
                                                            <Box component="span" sx={{ color: '#dc2626', ml: 0.5, fontSize: 11 }}>
                                                                ({record.lateMinutes}m late)
                                                            </Box>
                                                        )}
                                                    </Typography>
                                                </Stack>

                                                {formatTime(record.clockOutTime) && (
                                                    <Stack direction="row" alignItems="center" spacing={0.5} mb={0.4}>
                                                        <AccessTime sx={{ fontSize: 13, color: '#3b82f6' }} />
                                                        <Typography fontSize={12} fontWeight={600} color="#3b82f6">
                                                            Out: {formatTime(record.clockOutTime)}
                                                        </Typography>
                                                    </Stack>
                                                )}

                                                {inLat && inLng && (
                                                    <Stack direction="row" alignItems="center" spacing={0.3} mb={0.2}>
                                                        <LocationOn sx={{ fontSize: 13, color: '#16a34a', flexShrink: 0 }} />
                                                        <Typography fontSize={11} component="a"
                                                            href={`https://maps.google.com/?q=${inLat},${inLng}`}
                                                            target="_blank" rel="noopener noreferrer"
                                                            sx={{ color: '#16a34a', textDecoration: 'underline', cursor: 'pointer' }}>
                                                            In: {parseFloat(inLat).toFixed(4)}, {parseFloat(inLng).toFixed(4)}
                                                        </Typography>
                                                    </Stack>
                                                )}

                                                {outLat && outLng && (
                                                    <Stack direction="row" alignItems="center" spacing={0.3} mb={0.2}>
                                                        <LocationOn sx={{ fontSize: 13, color: '#3b82f6', flexShrink: 0 }} />
                                                        <Typography fontSize={11} component="a"
                                                            href={`https://maps.google.com/?q=${outLat},${outLng}`}
                                                            target="_blank" rel="noopener noreferrer"
                                                            sx={{ color: '#3b82f6', textDecoration: 'underline', cursor: 'pointer' }}>
                                                            Out: {parseFloat(outLat).toFixed(4)}, {parseFloat(outLng).toFixed(4)}
                                                        </Typography>
                                                    </Stack>
                                                )}

                                                {record.clockInDistanceMeters != null && (
                                                    <Typography fontSize={11} color="text.secondary" mt={0.2}>
                                                        📍 {Math.round(record.clockInDistanceMeters)}m from branch
                                                    </Typography>
                                                )}

                                                {late && record.lateReason && (
                                                    <Box sx={{ mt: 1, p: 1, bgcolor: '#fef2f2', borderRadius: 1.5, border: '1px solid #fca5a5' }}>
                                                        <Typography fontSize={11} color="#dc2626" fontWeight={600}>Reason:</Typography>
                                                        <Typography fontSize={11} color="#7f1d1d">{record.lateReason}</Typography>
                                                    </Box>
                                                )}
                                                {late && !record.lateReason && (
                                                    <Box sx={{ mt: 1, p: 1, bgcolor: '#fef2f2', borderRadius: 1.5, border: '1px dashed #fca5a5' }}>
                                                        <Typography fontSize={11} color="#dc2626">No reason provided</Typography>
                                                    </Box>
                                                )}
                                            </Paper>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    )}

                    {notClockedIn.length > 0 && (
                        <Box>
                            <Typography fontWeight={700} fontSize={13} color="text.secondary" mb={1.5}
                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Not Clocked In — {notClockedIn.length} employees
                            </Typography>
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                {notClockedIn.map((record, idx) => (
                                    <Chip key={record.id || idx} icon={<Person />}
                                        label={`${record.employeeName || 'Unknown'}${record.branchName ? ` (${record.branchName})` : ''}`}
                                        variant="outlined"
                                        sx={{ color: '#6b7280', borderColor: '#e5e7eb' }}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}
                </>
            )}
        </Paper>
    );
};

/* ═══════════════════════════════════════════════════════════════
    AM-APPROVED HISTORY — with date + branch filters
═══════════════════════════════════════════════════════════════ */
const AMApprovedHistorySection = ({ leaves = [], attendance = [], missed = [], sudden = [] }) => {
    const [dateFilter, setDateFilter] = useState('');
    const [branchFilter, setBranchFilter] = useState('ALL');

    const safeLeaves = Array.isArray(leaves) ? leaves : [];
    const safeAttendance = Array.isArray(attendance) ? attendance : [];
    const safeMissed = Array.isArray(missed) ? missed : [];
    const safeSudden = Array.isArray(sudden) ? sudden : [];

    const allBranches = ['ALL', ...Array.from(new Set([
        ...safeLeaves.map(i => i?.branchName),
        ...safeAttendance.map(i => i?.branchName),
        ...safeMissed.map(i => i?.branchName),
        ...safeSudden.map(i => i?.branchName),
    ].filter(Boolean)))];

    const applyFilters = (list, dateField = 'date') => list.filter(item => {
        if (!item) return false;
        if (dateFilter && item[dateField] && item[dateField] !== dateFilter) return false;
        if (branchFilter !== 'ALL' && item?.branchName !== branchFilter) return false;
        return true;
    });

    const filteredLeaves = applyFilters(safeLeaves, 'startDate');
    const filteredAttendance = applyFilters(safeAttendance, 'date');
    const filteredMissed = applyFilters(safeMissed, 'date');
    const filteredSudden = applyFilters(safeSudden, 'requestDate');

    const hasAny = safeLeaves.length + safeAttendance.length + safeMissed.length + safeSudden.length > 0;
    const hasFiltered = filteredLeaves.length + filteredAttendance.length + filteredMissed.length + filteredSudden.length > 0;

    return (
        <Paper sx={{ p: 2, mt: 3, borderRadius: 3, border: '1px dashed #cbd5e1', bgcolor: '#f8fafc' }}>
            <Typography variant="h6" fontWeight={800} mb={0.5}>AM-Approved History (View Only)</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Already approved by the Approval Manager — shown for records only. No action needed.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} alignItems="center" flexWrap="wrap">
                <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarMonth sx={{ fontSize: 18, color: '#6366f1' }} />
                    <TextField type="date" size="small" label="Date" value={dateFilter}
                        onChange={e => setDateFilter(e.target.value)}
                        InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
                </Stack>

                {allBranches.length > 2 && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FilterList sx={{ fontSize: 18, color: '#6366f1' }} />
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Branch</InputLabel>
                            <Select value={branchFilter} label="Branch"
                                onChange={e => setBranchFilter(e.target.value)}>
                                {allBranches.map(b => (
                                    <MenuItem key={b} value={b}>{b === 'ALL' ? 'All Branches' : b}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                )}

                {(dateFilter || branchFilter !== 'ALL') && (
                    <Button size="small" variant="text"
                        onClick={() => { setDateFilter(''); setBranchFilter('ALL'); }}>
                        Reset Filters
                    </Button>
                )}

                <Stack direction="row" spacing={1} ml="auto" flexWrap="wrap">
                    <Chip size="small" label={`${filteredAttendance.length} Attendance`} variant="outlined" />
                    <Chip size="small" label={`${filteredMissed.length} Missed Punch`} variant="outlined" />
                    <Chip size="small" label={`${filteredLeaves.length} Leave`} variant="outlined" />
                    <Chip size="small" label={`${filteredSudden.length} Sudden`} variant="outlined" />
                </Stack>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {!hasAny ? (
                <Alert severity="info">No AM-approved records yet</Alert>
            ) : !hasFiltered ? (
                <Alert severity="info">No records match the selected filters.</Alert>
            ) : (
                <Stack spacing={3}>
                    {filteredAttendance.length > 0 && (
                        <Box>
                            <Typography fontWeight={700} mb={1}>Attendance ({filteredAttendance.length})</Typography>
                            <Table size="small">
                                <TableHead sx={{ backgroundColor: '#f1f5f9' }}>
                                    <TableRow>
                                        {['Employee', 'Branch', 'Date', 'Late Reason', 'AM Remark', 'AM Acted By', 'Status'].map(h => (
                                            <TableCell key={h}><b>{h}</b></TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredAttendance.map((item, idx) => (
                                        <TableRow key={item?.id || idx} hover>
                                            <TableCell>{fmt(item?.employeeName)}</TableCell>
                                            <TableCell>{fmt(item?.branchName)}</TableCell>
                                            <TableCell>{fmt(item?.date)}</TableCell>
                                            <TableCell sx={{ maxWidth: 160 }}><Typography fontSize={12}>{fmt(item?.lateReason)}</Typography></TableCell>
                                            <TableCell sx={{ maxWidth: 200 }}><Typography fontSize={12}>{fmt(item?.approvalManagerRemark)}</Typography></TableCell>
                                            <TableCell>{fmt(item?.approvalManagerActedBy)}</TableCell>
                                            <TableCell><Chip label={statusLabel(item?.status)} size="small" color="success" /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}

                    {filteredMissed.length > 0 && (
                        <Box>
                            <Typography fontWeight={700} mb={1}>Missed Punch ({filteredMissed.length})</Typography>
                            <Table size="small">
                                <TableHead sx={{ backgroundColor: '#f1f5f9' }}>
                                    <TableRow>
                                        {['Employee', 'Branch', 'Date', 'Type', 'Clock In', 'Clock Out', 'Reason', 'AM Remark', 'AM Acted By', 'Status'].map(h => (
                                            <TableCell key={h}><b>{h}</b></TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredMissed.map((item, idx) => (
                                        <TableRow key={item?.attendanceId || item?.id || idx} hover>
                                            <TableCell>{fmt(item?.employeeName)}</TableCell>
                                            <TableCell>{fmt(item?.branchName)}</TableCell>
                                            <TableCell>{fmt(item?.date)}</TableCell>
                                            <TableCell><Chip label={fmt(item?.punchType)} size="small" variant="outlined" /></TableCell>
                                            <TableCell>{fmt(item?.requestedClockIn)}</TableCell>
                                            <TableCell>{fmt(item?.requestedClockOut)}</TableCell>
                                            <TableCell sx={{ maxWidth: 140 }}><Typography fontSize={12}>{fmt(item?.reason)}</Typography></TableCell>
                                            <TableCell sx={{ maxWidth: 200 }}><Typography fontSize={12}>{fmt(item?.approvalManagerRemarks)}</Typography></TableCell>
                                            <TableCell>{fmt(item?.approvalManagerApprovedBy)}</TableCell>
                                            <TableCell><Chip label={statusLabel(item?.status)} size="small" color="success" /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}

                    {filteredLeaves.length > 0 && (
                        <Box>
                            <Typography fontWeight={700} mb={1}>Leave ({filteredLeaves.length})</Typography>
                            <Table size="small">
                                <TableHead sx={{ backgroundColor: '#f1f5f9' }}>
                                    <TableRow>
                                        {['Employee', 'Branch', 'Dates', 'Reason', 'AM Remark', 'AM Acted By', 'AM Acted At', 'Status'].map(h => (
                                            <TableCell key={h}><b>{h}</b></TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredLeaves.map((item, idx) => (
                                        <TableRow key={item?.id || idx} hover>
                                            <TableCell>{fmt(item?.employeeName)}</TableCell>
                                            <TableCell>{fmt(item?.branchName)}</TableCell>
                                            <TableCell>{fmt(item?.startDate)} → {fmt(item?.endDate)}</TableCell>
                                            <TableCell sx={{ maxWidth: 160 }}><Typography fontSize={12}>{fmt(item?.reason)}</Typography></TableCell>
                                            <TableCell sx={{ maxWidth: 200 }}><Typography fontSize={12}>{fmt(item?.approvalManagerRemark)}</Typography></TableCell>
                                            <TableCell>{fmt(item?.approvalManagerActedBy)}</TableCell>
                                            <TableCell>{fmt(item?.approvalManagerActedAt)}</TableCell>
                                            <TableCell><Chip label={statusLabel(item?.status)} size="small" color="success" /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}

                    {filteredSudden.length > 0 && (
                        <Box>
                            <Typography fontWeight={700} mb={1}>Sudden Requests ({filteredSudden.length})</Typography>
                            <Table size="small">
                                <TableHead sx={{ backgroundColor: '#f1f5f9' }}>
                                    <TableRow>
                                        {['Employee', 'Branch', 'Date', 'Type', 'Reason', 'AM Remark', 'AM Acted By', 'Paid?', 'Status'].map(h => (
                                            <TableCell key={h}><b>{h}</b></TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredSudden.map((item, idx) => (
                                        <TableRow key={item?.id || idx} hover>
                                            <TableCell>{fmt(item?.employeeName)}</TableCell>
                                            <TableCell>{fmt(item?.branchName)}</TableCell>
                                            <TableCell>{fmt(item?.requestDate)}</TableCell>
                                            <TableCell><Chip label={fmt(item?.requestType?.replace('_', ' '))} size="small" variant="outlined" /></TableCell>
                                            <TableCell sx={{ maxWidth: 140 }}><Typography fontSize={12}>{fmt(item?.reason)}</Typography></TableCell>
                                            <TableCell sx={{ maxWidth: 200 }}><Typography fontSize={12}>{fmt(item?.approvalManagerRemark)}</Typography></TableCell>
                                            <TableCell>{fmt(item?.approvalManagerActedBy)}</TableCell>
                                            <TableCell><Chip label={item?.isPaid ? 'Paid' : 'Unpaid'} size="small" color={item?.isPaid ? 'success' : 'default'} /></TableCell>
                                            <TableCell><Chip label={statusLabel(item?.status)} size="small" color="success" /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                </Stack>
            )}
        </Paper>
    );
};

/* ═══════════════════════════════════════════════════════════════
    ATTENDANCE QUEUE
═══════════════════════════════════════════════════════════════ */
const AttendanceQueue = ({ data = [], onAction, onImageClick, allowedStatuses = ["PENDING_MANAGER", "PENDING"] }) => {
    const [dateFilter, setDateFilter] = useState(getTodayStr());
    const [branchFilter, setBranchFilter] = useState('ALL');

    const safeData = Array.isArray(data) ? data : [];
    const branches = ['ALL', ...Array.from(new Set(safeData.map(i => i?.branchName).filter(Boolean)))];

    const isPending = (item) => {
        const s = String(item?.status || item?.approvalStatus || '').toUpperCase();
        const allowed = allowedStatuses.map(s => s.toUpperCase());
        return allowed.includes(s);
    };

    const totalPending = safeData.filter(isPending).length;

    const pendingData = safeData.filter(item => {
        if (!item || !isPending(item)) return false;
        if (dateFilter && item.date && item.date !== dateFilter) return false;
        if (branchFilter !== 'ALL' && item.branchName !== branchFilter) return false;
        return true;
    });

    return (
        <Paper sx={{ p: 2, mt: 2, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }} mb={2} gap={1} flexWrap="wrap">
                <Typography variant="h6" fontWeight={700}>
                    Attendance Requests ({pendingData.length} shown / {totalPending} total)
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    <TextField type="date" size="small" label="Date" value={dateFilter}
                        onChange={e => setDateFilter(e.target.value)}
                        InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                    {branches.length > 2 && (
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Branch</InputLabel>
                            <Select value={branchFilter} label="Branch"
                                onChange={e => setBranchFilter(e.target.value)}>
                                {branches.map(b => (
                                    <MenuItem key={b} value={b}>{b === 'ALL' ? 'All Branches' : b}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {(dateFilter !== getTodayStr() || branchFilter !== 'ALL') && (
                        <Button size="small" variant="text"
                            onClick={() => { setDateFilter(getTodayStr()); setBranchFilter('ALL'); }}>
                            Reset
                        </Button>
                    )}
                </Stack>
            </Stack>

            {pendingData.length === 0 ? (
                <Alert severity="info">No attendance requests for the selected filters.</Alert>
            ) : (
                <Stack spacing={1.5}>
                    {pendingData.map((item, idx) => {
                        const late = isLateRecord(item);
                        const hasInSelfie = !!item?.clockInSelfieUrl;
                        const inLat = item?.clockInLatitude;
                        const inLng = item?.clockInLongitude;
                        const mapsUrl = inLat && inLng ? `https://maps.google.com/?q=${inLat},${inLng}` : null;
                        const escalated = isEscalated(item);

                        return (
                            <Paper key={item?.id || item?.attendanceId || idx} elevation={0} sx={{
                                p: 2, borderRadius: 2.5,
                                border: `1.5px solid ${escalated ? '#fcd34d' : late ? '#fca5a5' : '#d1fae5'}`,
                                bgcolor: escalated ? '#fffdf0' : late ? '#fff7f7' : '#f9fffe',
                            }}>
                                {/* Escalation banner */}
                                {escalated && (
                                    <Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#fffbeb', borderRadius: 1.5, border: '1px solid #fcd34d' }}>
                                        <Typography fontSize={12} color="#b45309" fontWeight={700}>
                                            ⏫ Escalated — Approval Manager did not act within 24 hours
                                        </Typography>
                                        <Stack direction="row" spacing={2} mt={0.5} flexWrap="wrap">
                                            <Typography fontSize={11} color="#92400e">
                                                Employee: <b>{fmt(item?.employeeName)}</b>
                                            </Typography>
                                            <Typography fontSize={11} color="#92400e">
                                                Branch: <b>{fmt(item?.branchName)}</b>
                                            </Typography>
                                            <Typography fontSize={11} color="#92400e">
                                                Date: <b>{fmt(item?.date)}</b>
                                            </Typography>
                                        </Stack>
                                        <Typography fontSize={11} color="#92400e" mt={0.3}>
                                            No AM remark — approval manager skipped this record
                                        </Typography>
                                    </Box>
                                )}

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                                    <Box sx={{ flexShrink: 0 }}>
                                        <Typography fontSize={10} color="text.secondary" fontWeight={600} mb={0.5}>
                                            CLOCK IN SELFIE
                                        </Typography>
                                        {hasInSelfie ? (
                                            <Tooltip title="Click to view & zoom" arrow>
                                                <Box onClick={() => onImageClick(`/attendance/selfie/${item.clockInSelfieUrl}`, item.employeeName, 'Clock In')}
                                                    sx={{ cursor: 'pointer', position: 'relative', '&:hover .zoom-hint': { opacity: 1 } }}>
                                                    <AuthenticatedImage
                                                        src={`/attendance/selfie/${item.clockInSelfieUrl}`}
                                                        size="100%"
                                                        sx={{
                                                            width: 90, height: 90, objectFit: 'cover',
                                                            border: `2px solid ${late ? '#fca5a5' : '#86efac'}`,
                                                            borderRadius: 2, display: 'block'
                                                        }}
                                                    />
                                                    <Box className="zoom-hint" sx={{
                                                        position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)',
                                                        borderRadius: 2, display: 'flex', alignItems: 'center',
                                                        justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s'
                                                    }}>
                                                        <ZoomIn sx={{ color: 'white', fontSize: 28 }} />
                                                    </Box>
                                                </Box>
                                            </Tooltip>
                                        ) : (
                                            <Box sx={{
                                                width: 90, height: 90, borderRadius: 2,
                                                bgcolor: '#f1f5f9', display: 'flex', flexDirection: 'column',
                                                alignItems: 'center', justifyContent: 'center',
                                                border: '1px dashed #cbd5e1', gap: 0.5
                                            }}>
                                                <CameraAlt sx={{ fontSize: 22, color: '#94a3b8' }} />
                                                <Typography fontSize={9} color="text.disabled" textAlign="center">No selfie</Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" alignItems="center" spacing={1} mb={0.5} flexWrap="wrap">
                                            <Typography fontWeight={700} fontSize={14}>{fmt(item?.employeeName)}</Typography>
                                            <Chip size="small"
                                                label={late ? 'LATE' : 'ON TIME'}
                                                icon={late
                                                    ? <Warning sx={{ fontSize: '12px !important' }} />
                                                    : <CheckCircle sx={{ fontSize: '12px !important' }} />}
                                                sx={{
                                                    bgcolor: late ? '#fef2f2' : '#f0fdf4',
                                                    color: late ? '#dc2626' : '#16a34a',
                                                    border: `1px solid ${late ? '#fca5a5' : '#86efac'}`,
                                                    fontWeight: 800, fontSize: 10
                                                }}
                                            />
                                            {item?.branchName && (
                                                <Chip size="small" label={item.branchName} variant="outlined" sx={{ fontSize: 10, color: '#6b7280' }} />
                                            )}
                                        </Stack>

                                        <Stack direction="row" alignItems="center" spacing={0.5} mb={0.4}>
                                            <AccessTime sx={{ fontSize: 13, color: late ? '#dc2626' : '#16a34a' }} />
                                            <Typography fontSize={12} color={late ? '#dc2626' : '#16a34a'} fontWeight={600}>
                                                {fmt(item?.date)}  |  In: {formatTime(item?.clockInTime) || fmt(item?.clockInTime)}
                                                {late && item?.lateMinutes > 0 && (
                                                    <Box component="span" sx={{ ml: 0.5, fontSize: 11 }}>
                                                        ({item.lateMinutes}m late)
                                                    </Box>
                                                )}
                                            </Typography>
                                        </Stack>

                                        {mapsUrl ? (
                                            <Stack direction="row" alignItems="center" spacing={0.3} mb={0.4}>
                                                <LocationOn sx={{ fontSize: 13, color: '#0284c7', flexShrink: 0 }} />
                                                <Typography fontSize={11} component="a"
                                                    href={mapsUrl} target="_blank" rel="noopener noreferrer"
                                                    sx={{ color: '#0284c7', textDecoration: 'underline', cursor: 'pointer' }}>
                                                    {parseFloat(inLat).toFixed(5)}, {parseFloat(inLng).toFixed(5)} — tap to open map
                                                </Typography>
                                            </Stack>
                                        ) : (
                                            <Typography fontSize={11} color="text.disabled" mb={0.4}>📍 No GPS location recorded</Typography>
                                        )}

                                        {item?.clockInDistanceMeters != null && (
                                            <Typography fontSize={11} color="text.secondary" mb={0.4}>
                                                📍 {Math.round(item.clockInDistanceMeters)}m from branch
                                            </Typography>
                                        )}

                                        {item?.lateReason && item.lateReason.trim() !== '' && (
                                            <Box sx={{ mt: 0.5, px: 1.5, py: 0.75, bgcolor: '#fef2f2', borderRadius: 1.5, border: '1px solid #fca5a5', display: 'inline-block' }}>
                                                <Typography fontSize={11} color="#dc2626" fontWeight={600}>
                                                    Reason: <Box component="span" fontWeight={400}>{item.lateReason}</Box>
                                                </Typography>
                                            </Box>
                                        )}

                                        {item?.approvalManagerRemark && (
                                            <Box sx={{ mt: 0.5 }}>
                                                <AMRemarkBadge remark={item.approvalManagerRemark} />
                                                <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.3 }}>
                                                    "{item.approvalManagerRemark}"
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    <Stack spacing={1} flexShrink={0} alignSelf={{ xs: 'flex-end', sm: 'center' }}>
                                        <Button size="small" variant="contained" color="success"
                                            onClick={() => onAction('attendance', item?.id || item?.attendanceId, 'APPROVED', item)}
                                            sx={{ minWidth: 90, fontWeight: 700 }}>
                                            Approve
                                        </Button>
                                        <Button size="small" variant="outlined" color="error"
                                            onClick={() => onAction('attendance', item?.id || item?.attendanceId, 'REJECTED', item)}
                                            sx={{ minWidth: 90 }}>
                                            Reject
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>
            )}
        </Paper>
    );
};

/* ═══════════════════════════════════════════════════════════════
    LEAVE QUEUE
═══════════════════════════════════════════════════════════════ */
const LeaveQueue = ({ data = [], onAction, allowedStatuses = ["PENDING_MANAGER", "PENDING"] }) => {
    const safeData = Array.isArray(data) ? data : [];
    const pendingData = safeData.filter(item => {
        if (!item) return false;
        const s = String(item.status || item.approvalStatus || '').toUpperCase();
        const allowed = allowedStatuses.map(s => s.toUpperCase());
        return allowed.includes(s);
    });

    return (
        <Paper sx={{ p: 2, mt: 2, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>
                Leave Requests ({pendingData.length})
            </Typography>
            {pendingData.length === 0 ? <Alert severity="info">No leave pending</Alert> : (
                pendingData.map((item, idx) => (
                    <Box key={item?.id || idx} sx={{
                        borderBottom: '1px solid #f1f5f9', py: 1.5,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'
                    }}>
                        <Box sx={{ flex: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" mb={0.5}>
                                <Typography fontWeight={600}>{fmt(item?.employeeName)}</Typography>
                                {item?.branchName && (
                                    <Chip size="small" label={item.branchName} variant="outlined"
                                        sx={{ fontSize: 10, color: '#6b7280' }} />
                                )}
                                {item?.designation && (
                                    <Typography fontSize={11} color="text.secondary">{item.designation}</Typography>
                                )}
                            </Stack>

                            <Typography fontSize={13}>{fmt(item?.startDate)} → {fmt(item?.endDate)}</Typography>
                            <Typography fontSize={12} color="text.secondary">Reason: {fmt(item?.reason)}</Typography>

                            {/* Escalated warning */}
                            {isEscalatedLeave(item) && (
                                <Box sx={{ mt: 1, p: 1.5, bgcolor: '#fffbeb', borderRadius: 1.5, border: '1px solid #fcd34d' }}>
                                    <Typography fontSize={12} color="#b45309" fontWeight={700}>
                                        ⏫ Escalated — Approval Manager did not act within 24 hours
                                    </Typography>
                                    <Stack direction="row" spacing={2} mt={0.5} flexWrap="wrap">
                                        <Typography fontSize={11} color="#92400e">
                                            Employee: <b>{fmt(item?.employeeName)}</b>
                                        </Typography>
                                        <Typography fontSize={11} color="#92400e">
                                            Branch: <b>{fmt(item?.branchName)}</b>
                                        </Typography>
                                    </Stack>
                                    <Typography fontSize={11} color="#92400e" mt={0.3}>
                                        No AM remark — approval manager skipped this record
                                    </Typography>
                                </Box>
                            )}

                            {item?.approvalManagerRemark && (
                                <Box sx={{ mt: 0.5 }}>
                                    <AMRemarkBadge remark={item.approvalManagerRemark} />
                                    <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.3 }}>
                                        "{item.approvalManagerRemark}"
                                        {item?.approvalManagerActedBy &&
                                            <span style={{ color: '#6b7280' }}> — {item.approvalManagerActedBy}</span>}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <Stack direction="row" spacing={1} flexShrink={0} ml={1}>
                            <Button size="small" variant="contained" color="success"
                                onClick={() => onAction('leave', item?.id, 'APPROVED', item)}>Approve</Button>
                            <Button size="small" variant="outlined" color="error"
                                onClick={() => onAction('leave', item?.id, 'REJECTED', item)}>Reject</Button>
                        </Stack>
                    </Box>
                ))
            )}
        </Paper>
    );
};

/* ═══════════════════════════════════════════════════════════════
    MISSED PUNCH QUEUE
═══════════════════════════════════════════════════════════════ */
const MissedPunchQueue = ({ data = [], onAction, allowedStatuses = ["PENDING_MANAGER", "PENDING"] }) => {
    const safeData = Array.isArray(data) ? data : [];
    const pendingData = safeData.filter(item => {
        if (!item) return false;
        const s = String(item.status || item.approvalStatus || '').toUpperCase();
        const allowed = allowedStatuses.map(s => s.toUpperCase());
        return allowed.includes(s) && !s.includes("APPROVED") && !s.includes("REJECTED");
    });

    return (
        <Paper sx={{ p: 2, mt: 2, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>Missed Punch Requests ({pendingData.length})</Typography>
            {pendingData.length === 0 ? <Alert severity="info">No missed punch pending</Alert> : (
                <Table size="small">
                    <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                        <TableRow>
                            {['Employee', 'Branch', 'Date', 'Type', 'Clock In', 'Clock Out', 'Reason', 'AM Remark / Escalation', 'Status', 'Action'].map(h => (
                                <TableCell key={h}><b>{h}</b></TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pendingData.map((item, idx) => {
                            const escalatedMissed = String(item?.status || '').toUpperCase() === 'PENDING_MANAGER' && !item?.approvalManagerRemarks;
                            return (
                                <TableRow key={item?.attendanceId || item?.id || idx} hover
                                    sx={{ bgcolor: escalatedMissed ? '#fffdf0' : 'inherit' }}>
                                    <TableCell>{fmt(item?.employeeName || item?.employee?.name)}</TableCell>
                                    <TableCell>{fmt(item?.branchName)}</TableCell>
                                    <TableCell>{fmt(item?.date || item?.attendanceDate)}</TableCell>
                                    <TableCell><Chip label={fmt(item?.punchType)} size="small" variant="outlined" /></TableCell>
                                    <TableCell>{fmt(item?.requestedClockIn)}</TableCell>
                                    <TableCell>{fmt(item?.requestedClockOut)}</TableCell>
                                    <TableCell sx={{ maxWidth: 140 }}><Typography fontSize={12}>{fmt(item?.reason)}</Typography></TableCell>
                                    <TableCell sx={{ maxWidth: 180 }}>
                                        {item?.approvalManagerRemarks ? (
                                            <Tooltip title={item.approvalManagerRemarks} arrow>
                                                <Chip size="small" icon={<InfoOutlined sx={{ fontSize: 13 }} />}
                                                    label="View AM Note"
                                                    sx={{ bgcolor: '#fef9c3', color: '#854d0e', border: '1px solid #fde047', fontWeight: 700, fontSize: 11 }} />
                                            </Tooltip>
                                        ) : escalatedMissed ? (
                                            <Tooltip title="Approval Manager did not act within 24h — escalated automatically" arrow>
                                                <Chip size="small" icon={<Warning sx={{ fontSize: 13 }} />}
                                                    label="⏫ Escalated"
                                                    sx={{ bgcolor: '#fffbeb', color: '#b45309', border: '1px solid #fcd34d', fontWeight: 700, fontSize: 11 }} />
                                            </Tooltip>
                                        ) : (
                                            <Typography fontSize={12} color="text.disabled">—</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell><Chip label={statusLabel(item?.status || item?.approvalStatus)} size="small" color="warning" /></TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Button size="small" variant="contained" color="success"
                                                onClick={() => onAction('missedPunch', item?.attendanceId || item?.id, 'APPROVED', item)}>
                                                Approve
                                            </Button>
                                            <Button size="small" variant="outlined" color="error"
                                                onClick={() => onAction('missedPunch', item?.attendanceId || item?.id, 'REJECTED', item)}>
                                                Reject
                                            </Button>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </Paper>
    );
};

/* ═══════════════════════════════════════════════════════════════
    MAIN WRAPPER
═══════════════════════════════════════════════════════════════ */
const HRApprovalCenter = () => {
    const { user } = useAuth();
    const [activeTabKey, setActiveTabKey] = useState('');
    const [selfieDate, setSelfieDate] = useState(getTodayStr());

    const [lightbox, setLightbox] = useState({ open: false, src: '', employeeName: '', title: '' });

    const openLightbox = (src, employeeName, title) => {
        setLightbox({ open: true, src, employeeName, title });
    };
    const closeLightbox = () => setLightbox({ open: false, src: '', employeeName: '', title: '' });

    const accessibleTabs = [];
    if (user?.roles?.includes('ROLE_MANAGER')) accessibleTabs.push({ key: 'manager', label: 'Manager' });
    if (user?.roles?.includes('ROLE_HR')) accessibleTabs.push({ key: 'hr', label: 'HR' });
    if (user?.roles?.includes('ROLE_OWNER')) accessibleTabs.push({ key: 'owner', label: 'Owner' });

    useEffect(() => {
        if (accessibleTabs.length > 0 && !activeTabKey) setActiveTabKey(accessibleTabs[0].key);
    }, [user, accessibleTabs, activeTabKey]);

    const [loading, setLoading] = useState(false);
    const [selfiesLoading, setSelfiesLoading] = useState(false);
    const [attManager, setAttManager] = useState([]);
    const [leaveManager, setLeaveManager] = useState([]);
    const [missedManager, setMissedManager] = useState([]);
    const [attHR, setAttHR] = useState([]);
    const [leaveHR, setLeaveHR] = useState([]);
    const [missedHR, setMissedHR] = useState([]);
    const [amLeaves, setAmLeaves] = useState([]);
    const [amAttendance, setAmAttendance] = useState([]);
    const [amMissed, setAmMissed] = useState([]);
    const [amSudden, setAmSudden] = useState([]);
    const [managerTodaySelfies, setManagerTodaySelfies] = useState([]);

    const fetchSelfies = useCallback(async (date) => {
        if (!user) return;
        const isManagerOrOwner = user?.roles?.includes('ROLE_MANAGER') || user?.roles?.includes('ROLE_OWNER');
        if (!isManagerOrOwner) return;
        setSelfiesLoading(true);
        try {
            const params = date ? { date } : {};
            const res = await api.get('/attendance/today-selfies-manager', { params });
            setManagerTodaySelfies(res.data || []);
        } catch {
            setManagerTodaySelfies([]);
        } finally {
            setSelfiesLoading(false);
        }
    }, [user]);

    const fetchData = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            if (user?.roles?.includes('ROLE_MANAGER') || user?.roles?.includes('ROLE_OWNER')) {
                await api.get('/attendance/pending-manager').then(r => setAttManager(r.data || [])).catch(() => setAttManager([]));
                await api.get('/leaves/pending/manager').then(r => setLeaveManager(r.data || [])).catch(() => setLeaveManager([]));
                await api.get('/missed-punch/manager').then(r => setMissedManager(r.data || [])).catch(() => setMissedManager([]));
                await api.get('/leaves/am-approved').then(r => setAmLeaves(r.data || [])).catch(() => setAmLeaves([]));
                await api.get('/attendance/am-approved').then(r => setAmAttendance(r.data || [])).catch(() => setAmAttendance([]));
                await api.get('/missed-punch/am-approved').then(r => setAmMissed(r.data || [])).catch(() => setAmMissed([]));
                await api.get('/sudden-requests/am-approved').then(r => setAmSudden(r.data || [])).catch(() => setAmSudden([]));
            }
            if (user?.roles?.includes('ROLE_HR') || user?.roles?.includes('ROLE_OWNER')) {
                await api.get('/attendance/pending-hr').then(r => setAttHR(r.data || [])).catch(() => setAttHR([]));
                await api.get('/leaves/pending/hr').then(r => setLeaveHR(r.data || [])).catch(() => setLeaveHR([]));
                await api.get('/missed-punch/hr').then(r => setMissedHR(r.data || [])).catch(() => setMissedHR([]));
            }
        } catch { toast.error('Failed to load approval queue.'); }
        finally { setLoading(false); }
    }, [user]);

    useEffect(() => { fetchData(); fetchSelfies(selfieDate); }, [fetchData, fetchSelfies, selfieDate]);

    const handleDateChange = (date) => {
        setSelfieDate(date);
        fetchSelfies(date);
    };

    const [modal, setModal] = useState({ open: false, type: '', id: null, status: '', remark: '', approvedPaidDays: 0, leaveItem: null });

    const openModal = (type, id, status, item = null) => {
        let leaveItem = null;
        if (type === 'leave' && item) { leaveItem = item; }
        setModal({ open: true, type, id, status, remark: '', approvedPaidDays: 0, leaveItem });
    };

    const handleSubmit = async () => {
        try {
            const roleContext = activeTabKey || 'owner';
            if (modal.type === 'attendance') {
                const targetStatus = roleContext === 'manager' || roleContext === 'owner'
                    ? (modal.status === 'APPROVED' ? 'APPROVED_BY_MANAGER' : 'REJECTED_BY_MANAGER')
                    : modal.status;
                await api.put(`/attendance/status/${modal.id}`, null, { params: { newStatus: targetStatus, remark: modal.remark } });
            }
            if (modal.type === 'leave') {
                await api.put(`/leaves/status/${modal.id}`, null, { params: { newStatus: modal.status, managerRemark: modal.remark, approvedPaidDays: modal.status === 'APPROVED' ? modal.approvedPaidDays : 0 } });
            }
            if (modal.type === 'missedPunch') {
                const isDS = roleContext === 'manager' || roleContext === 'owner';
                const ep = isDS
                    ? `/missed-punch/manager/${modal.status === 'APPROVED' ? 'approve' : 'reject'}/${modal.id}`
                    : `/missed-punch/hr/${modal.status === 'APPROVED' ? 'approve' : 'reject'}/${modal.id}`;
                await api.post(`${ep}?remarks=${encodeURIComponent(modal.remark)}`);
            }
            toast.success('Updated successfully');
            setModal({ open: false, type: '', id: null, status: '', remark: '', approvedPaidDays: 0, leaveItem: null });
            fetchData();
            fetchSelfies(selfieDate);
        } catch (e) { toast.error('Error: ' + (e?.response?.data?.message || e.message)); }
    };

    const maxPaidDays = modal.leaveItem?.startDate && modal.leaveItem?.endDate
        ? Math.max(1, Math.round((new Date(modal.leaveItem.endDate) - new Date(modal.leaveItem.startDate)) / 86400000) + 1)
        : 1;

    const currentTabIndex = accessibleTabs.findIndex(t => t.key === activeTabKey);

    return (
        <Box sx={{ p: 4 }}>
            <Typography variant="h4" fontWeight={800} mb={2}>Approval Center</Typography>

            {accessibleTabs.length > 0 && (
                <Tabs value={currentTabIndex >= 0 ? currentTabIndex : 0}
                    onChange={(_, v) => setActiveTabKey(accessibleTabs[v].key)} sx={{ mb: 1 }}>
                    {accessibleTabs.map(t => <Tab key={t.key} label={t.label} />)}
                </Tabs>
            )}

            {loading ? <CircularProgress sx={{ mt: 4 }} /> : (
                <Box>
                    {activeTabKey === 'manager' && (
                        <>
                            <TodaySelfiesSection
                                records={managerTodaySelfies} loading={selfiesLoading}
                                onRefresh={() => fetchSelfies(selfieDate)}
                                selectedDate={selfieDate} onDateChange={handleDateChange}
                                onImageClick={openLightbox}
                            />
                            <AttendanceQueue data={attManager} onAction={openModal} onImageClick={openLightbox} />
                            <LeaveQueue data={leaveManager} onAction={openModal} />
                            <MissedPunchQueue data={missedManager} onAction={openModal} />
                            <AMApprovedHistorySection leaves={amLeaves} attendance={amAttendance} missed={amMissed} sudden={amSudden} />
                        </>
                    )}
                    {activeTabKey === 'hr' && (
                        <>
                            <AttendanceQueue data={attHR} onAction={openModal} onImageClick={openLightbox} allowedStatuses={['PENDING_HR']} />
                            <LeaveQueue data={leaveHR} onAction={openModal} allowedStatuses={['PENDING_HR']} />
                            <MissedPunchQueue data={missedHR} onAction={openModal} allowedStatuses={['PENDING_HR']} />
                        </>
                    )}
                    {activeTabKey === 'owner' && (
                        <>
                            <TodaySelfiesSection
                                records={managerTodaySelfies} loading={selfiesLoading}
                                onRefresh={() => fetchSelfies(selfieDate)}
                                selectedDate={selfieDate} onDateChange={handleDateChange}
                                onImageClick={openLightbox}
                            />
                            <AttendanceQueue data={attManager.length > 0 ? attManager : attHR} onAction={openModal} onImageClick={openLightbox} />
                            <LeaveQueue data={leaveManager.length > 0 ? leaveManager : leaveHR} onAction={openModal} />
                            <MissedPunchQueue data={missedManager.length > 0 ? missedManager : missedHR} onAction={openModal} />
                            <AMApprovedHistorySection leaves={amLeaves} attendance={amAttendance} missed={amMissed} sudden={amSudden} />
                        </>
                    )}
                </Box>
            )}

            {/* Confirmation Modal */}
            <Modal open={modal.open} onClose={() => setModal({ ...modal, open: false })}>
                <Box sx={MODAL_STYLE}>
                    <Typography variant="h6" mb={2}>Confirm — {modal.status}</Typography>
                    <TextField fullWidth multiline rows={3} label="Remark *" value={modal.remark}
                        onChange={e => setModal({ ...modal, remark: e.target.value })} sx={{ mb: 2 }} />
                    {modal.type === 'leave' && modal.status === 'APPROVED' && (
                        <TextField fullWidth type="number" label="Approved Paid Days"
                            value={modal.approvedPaidDays}
                            inputProps={{ min: 0, max: maxPaidDays }}
                            onChange={e => setModal({ ...modal, approvedPaidDays: Number(e.target.value) })}
                            sx={{ mb: 2 }} />
                    )}
                    <Button variant="contained" fullWidth
                        color={modal.status === 'APPROVED' ? 'success' : 'error'}
                        disabled={!modal.remark?.trim()}
                        onClick={handleSubmit}>
                        Confirm {modal.status}
                    </Button>
                </Box>
            </Modal>

            {/* Full Image Lightbox with Zoom */}
            <ImageLightbox
                open={lightbox.open}
                src={lightbox.src}
                employeeName={lightbox.employeeName}
                title={lightbox.title}
                onClose={closeLightbox}
            />
        </Box>
    );
};

export default HRApprovalCenter;